import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Checkbox from '../../../components/ui/Checkbox';

const SettingsSection = ({ user, onUpdateProfile }) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '+63 912 345 6789',
    location: 'Manila, Philippines',
    bio: 'Travel enthusiast exploring the beautiful Philippines',
    language: 'en',
    currency: 'PHP',
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    marketingEmails: true,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onUpdateProfile(formData);
  };

  const sections = [
    { id: 'profile', label: 'Profile Information', icon: 'User' },
    { id: 'preferences', label: 'Travel Preferences', icon: 'Settings' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'privacy', label: 'Privacy', icon: 'Lock' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'tl', label: 'Tagalog' },
    { value: 'es', label: 'Spanish' }
  ];

  const currencyOptions = [
    { value: 'PHP', label: 'Philippine Peso (₱)' },
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-xl border border-border p-4 sticky top-24">
            <h3 className="text-lg font-bold text-foreground mb-4">Settings</h3>
            <nav className="space-y-1">
              {sections?.map((section) => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-250 ${
                    activeSection === section?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={section?.icon} size={18} />
                  <span className="text-sm font-medium">{section?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <div className="bg-card rounded-xl border border-border p-6 md:p-8">
            <form onSubmit={handleSubmit}>
              {activeSection === 'profile' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">Profile Information</h2>
                    <p className="text-sm text-muted-foreground">
                      Update your personal details and profile information
                    </p>
                  </div>

                  <div className="flex items-center gap-6 pb-6 border-b border-border">
                    <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold">
                      {formData?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <Button variant="outline" size="sm" iconName="Upload" iconPosition="left">
                        Change Photo
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        JPG, PNG or GIF. Max size 2MB
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Full Name"
                      type="text"
                      value={formData?.name}
                      onChange={(e) => handleChange('name', e?.target?.value)}
                      required
                    />
                    <Input
                      label="Email Address"
                      type="email"
                      value={formData?.email}
                      onChange={(e) => handleChange('email', e?.target?.value)}
                      required
                    />
                    <Input
                      label="Phone Number"
                      type="tel"
                      value={formData?.phone}
                      onChange={(e) => handleChange('phone', e?.target?.value)}
                    />
                    <Input
                      label="Location"
                      type="text"
                      value={formData?.location}
                      onChange={(e) => handleChange('location', e?.target?.value)}
                    />
                  </div>

                  <Input
                    label="Bio"
                    type="text"
                    value={formData?.bio}
                    onChange={(e) => handleChange('bio', e?.target?.value)}
                    description="Tell us a bit about yourself and your travel interests"
                  />

                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" type="button">Cancel</Button>
                    <Button variant="default" type="submit">Save Changes</Button>
                  </div>
                </div>
              )}

              {activeSection === 'preferences' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">Travel Preferences</h2>
                    <p className="text-sm text-muted-foreground">
                      Customize your travel experience and display settings
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Select
                      label="Preferred Language"
                      options={languageOptions}
                      value={formData?.language}
                      onChange={(value) => handleChange('language', value)}
                    />
                    <Select
                      label="Currency"
                      options={currencyOptions}
                      value={formData?.currency}
                      onChange={(value) => handleChange('currency', value)}
                    />
                  </div>

                  <div className="space-y-4 pt-4 border-t border-border">
                    <h3 className="text-lg font-semibold text-foreground">Travel Interests</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      <Checkbox label="Beach & Islands" checked />
                      <Checkbox label="Mountain Adventures" checked />
                      <Checkbox label="Cultural Heritage" />
                      <Checkbox label="Food & Cuisine" checked />
                      <Checkbox label="Water Sports" />
                      <Checkbox label="Wildlife & Nature" checked />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" type="button">Cancel</Button>
                    <Button variant="default" type="submit">Save Preferences</Button>
                  </div>
                </div>
              )}

              {activeSection === 'notifications' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">Notification Settings</h2>
                    <p className="text-sm text-muted-foreground">
                      Manage how you receive updates and notifications
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <Checkbox
                        label="Email Notifications"
                        description="Receive trip updates and booking confirmations via email"
                        checked={formData?.emailNotifications}
                        onChange={(e) => handleChange('emailNotifications', e?.target?.checked)}
                      />
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <Checkbox
                        label="Push Notifications"
                        description="Get real-time alerts about your trips and bookings"
                        checked={formData?.pushNotifications}
                        onChange={(e) => handleChange('pushNotifications', e?.target?.checked)}
                      />
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <Checkbox
                        label="SMS Notifications"
                        description="Receive important updates via text message"
                        checked={formData?.smsNotifications}
                        onChange={(e) => handleChange('smsNotifications', e?.target?.checked)}
                      />
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <Checkbox
                        label="Marketing Emails"
                        description="Get travel deals, tips, and destination recommendations"
                        checked={formData?.marketingEmails}
                        onChange={(e) => handleChange('marketingEmails', e?.target?.checked)}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" type="button">Cancel</Button>
                    <Button variant="default" type="submit">Save Settings</Button>
                  </div>
                </div>
              )}

              {activeSection === 'security' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">Security Settings</h2>
                    <p className="text-sm text-muted-foreground">
                      Manage your password and account security
                    </p>
                  </div>

                  <div className="space-y-4">
                    <Input
                      label="Current Password"
                      type="password"
                      value={formData?.currentPassword}
                      onChange={(e) => handleChange('currentPassword', e?.target?.value)}
                      placeholder="Enter current password"
                    />
                    <Input
                      label="New Password"
                      type="password"
                      value={formData?.newPassword}
                      onChange={(e) => handleChange('newPassword', e?.target?.value)}
                      placeholder="Enter new password"
                      description="Must be at least 8 characters with letters and numbers"
                    />
                    <Input
                      label="Confirm New Password"
                      type="password"
                      value={formData?.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
                      placeholder="Confirm new password"
                    />
                  </div>

                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-start gap-3">
                      <Icon name="Shield" size={20} className="text-success mt-1" />
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Two-Factor Authentication</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          Add an extra layer of security to your account
                        </p>
                        <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
                          Enable 2FA
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" type="button">Cancel</Button>
                    <Button variant="default" type="submit">Update Password</Button>
                  </div>
                </div>
              )}

              {activeSection === 'privacy' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">Privacy Settings</h2>
                    <p className="text-sm text-muted-foreground">
                      Control your data and privacy preferences
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <Checkbox
                        label="Profile Visibility"
                        description="Allow other travelers to view your profile and trip reviews"
                        checked
                      />
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <Checkbox
                        label="Activity Tracking"
                        description="Help us improve recommendations based on your browsing activity"
                        checked
                      />
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <Checkbox
                        label="Data Sharing"
                        description="Share anonymized data with partners to enhance travel services"
                      />
                    </div>
                  </div>

                  <div className="p-4 border border-error/30 rounded-lg bg-error/5">
                    <div className="flex items-start gap-3">
                      <Icon name="AlertTriangle" size={20} className="text-error mt-1" />
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Delete Account</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          Permanently delete your account and all associated data
                        </p>
                        <Button variant="destructive" size="sm" iconName="Trash2" iconPosition="left">
                          Delete Account
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" type="button">Cancel</Button>
                    <Button variant="default" type="submit">Save Privacy Settings</Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsSection;